<template>
  <v-container :class="`d-flex align-center ${permitirEdicao ? 'pr-1' : ''}`">
    <canvas
      id="sig-canvas"
      ref="canvasRef"
      width="600"
      height="200"
      @mousedown="handleCanvasMouseDown"
      @mouseup="handleCanvasMouseUp"
      @mousemove="handleCanvasMouseMove"
      @touchstart="handleCanvasTouchStart"
      @touchmove="handleCanvasTouchMove"
      @touchend="handleCanvasTouchEnd"
    />

    <div
      v-if="permitirEdicao"
      class="d-flex flex-column ml-1"
    >
      <v-btn
        fab
        x-small
        class="mb-1"
        @click="handleSubmitBtnClick"
      >
        <v-icon>
          {{ icons.mdiContentSave }}
        </v-icon>
      </v-btn>

      <v-btn
        fab
        x-small
        @click="handleClearBtnClick"
      >
        <v-icon>
          {{ icons.mdiDelete }}
        </v-icon>
      </v-btn>
    </div>
  </v-container>
</template>

<style>
#sig-canvas {
  border-radius: 15px;
  cursor: crosshair;
  border: 1px dashed var(--v-primary-base);
  background-color: white;
}
</style>

<script>
import { onMounted, ref, watchEffect } from '@vue/composition-api'
import { mdiContentSave, mdiDelete } from '@mdi/js'

export default {
  props: {
    permitirEdicao: Boolean,
    signatureUrl: {
      type: String,
      default: null,
    },
  },

  setup(props, { emit }) {
    window.requestAnimFrame = (function (callback) {
      return window.requestAnimationFrame
        || window.webkitRequestAnimationFrame
        || window.mozRequestAnimationFrame
        || window.oRequestAnimationFrame
        || window.msRequestAnimaitonFrame
        || function () {
          window.setTimeout(callback, 1000 / 60)
        }
    }())

    const canvasRef = ref({})
    const ctxRef = ref({})

    onMounted(() => {
      ctxRef.value = document.getElementById('sig-canvas').getContext('2d')
      ctxRef.value.strokeStyle = '#222222'
      ctxRef.value.lineWidth = 2
    })

    const drawing = ref(false)
    const mousePos = ref({ x: 0, y: 0 })
    const lastPos = ref(mousePos.value)

    function getMousePos(canvasDom, mouseEvent) {
      const rect = canvasDom.getBoundingClientRect()
      return {
        x: mouseEvent.clientX - rect.left,
        y: mouseEvent.clientY - rect.top,
      }
    }

    function getTouchPos(canvasDom, touchEvent) {
      const rect = canvasDom.getBoundingClientRect()
      return {
        x: touchEvent.touches[0].clientX - rect.left,
        y: touchEvent.touches[0].clientY - rect.top,
      }
    }

    function renderCanvas() {
      if (drawing.value) {
        ctxRef.value.moveTo(lastPos.value.x, lastPos.value.y)
        ctxRef.value.lineTo(mousePos.value.x, mousePos.value.y)
        ctxRef.value.stroke()
        lastPos.value = { ...mousePos.value }
      }
    }

    function handleCanvasMouseDown(e) {
      drawing.value = true
      lastPos.value = getMousePos(canvasRef.value, e)
    }

    function handleCanvasMouseUp() {
      drawing.value = false
    }

    function handleCanvasMouseMove(e) {
      mousePos.value.x = e.clientX - canvasRef.value.getBoundingClientRect().left
      mousePos.value.y = e.clientY - canvasRef.value.getBoundingClientRect().top
    }

    function handleCanvasTouchStart(e) {
      mousePos.value = getTouchPos(canvasRef.value, e)
      const touch = e.touches[0]
      const me = new MouseEvent('mousedown', {
        clientX: touch.clientX,
        clientY: touch.clientY,
      })
      canvasRef.value.dispatchEvent(me)
    }

    function handleCanvasTouchMove(e) {
      const touch = e.touches[0]
      const me = new MouseEvent('mousemove', {
        clientX: touch.clientX,
        clientY: touch.clientY,
      })
      canvasRef.value.dispatchEvent(me)
    }

    function handleCanvasTouchEnd() {
      const me = new MouseEvent('mouseup', {})
      canvasRef.value.dispatchEvent(me)
    }

    function drawLoop() {
      window.requestAnimFrame(drawLoop)
      renderCanvas()
    }
    drawLoop()

    function clearCanvas() {
      // eslint-disable-next-line no-self-assign
      canvasRef.value.width = canvasRef.value.width
    }

    function handleClearBtnClick() {
      clearCanvas()
    }

    function handleSubmitBtnClick() {
      emit('input-changed', canvasRef.value.toDataURL())
    }

    function loadImage(url) {
      const img = new Image()
      img.crossOrigin = 'anonymous' // Set crossOrigin to indicate that the image may be tainted
      img.src = url

      img.onload = () => {
        ctxRef.value.fillStyle = 'white'
        ctxRef.value.fillRect(0, 0, canvasRef.value.width, canvasRef.value.height)

        // Calculate scaling factors for the image
        const scaleX = canvasRef.value.width / img.width
        const scaleY = canvasRef.value.height / img.height
        const scale = Math.min(scaleX, scaleY)

        // Calculate position to center the image on the canvas
        const offsetX = (canvasRef.value.width - img.width * scale) / 2
        const offsetY = (canvasRef.value.height - img.height * scale) / 2

        // Draw the image onto the canvas
        ctxRef.value.drawImage(img, offsetX, offsetY, img.width * scale, img.height * scale)
      }

      img.onerror = () => {
        console.error('Error loading image:', url)
      }
    }

    watchEffect(() => {
      if (!canvasRef.value.style) return

      if (props.signatureUrl) {
        loadImage(props.signatureUrl)
      } else {
        clearCanvas()
      }
    })

    watchEffect(() => {
      if (!canvasRef.value.style) return

      if (props.permitirEdicao) {
        canvasRef.value.style.pointerEvents = 'auto' // Enable canvas painting
      } else {
        canvasRef.value.style.pointerEvents = 'none'
      }
    })

    return {
      canvasRef,
      handleCanvasMouseDown,
      handleCanvasMouseUp,
      handleCanvasMouseMove,
      handleCanvasTouchStart,
      handleCanvasTouchMove,
      handleCanvasTouchEnd,
      handleClearBtnClick,
      handleSubmitBtnClick,
      icons: {
        mdiContentSave,
        mdiDelete,
      },
    }
  },
}
</script>
